import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { ReactComponent as CancelIconX } from '../res/assets/cancelIconX.svg';
import colors from '../res/styles/Colors';
import dimension from '../res/styles/Responsive';

export default class PhotoCarousel extends Component {
    static propTypes = {
        isVisible: PropTypes.bool,
        photosList: PropTypes.arrayOf(PropTypes.object),
        initialPhotoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        onClose: PropTypes.func
    };

    static defaultProps = {
        isVisible: false,
        photosList: [],
        initialPhotoId: null,
        onClose: () => { }
    };

    constructor(props) {
        super(props);
        this.state = {
            activeIndex: this.getInitialIndex(props)
        };
    }

    componentDidUpdate(prevProps) {
        // Reset to initial index when carousel opens
        if (!prevProps.isVisible && this.props.isVisible) {
            this.setState({ activeIndex: this.getInitialIndex(this.props) });
        }
    }

    getInitialIndex(props) {
        if (props.initialPhotoId && props.photosList.length > 0) {
            const index = props.photosList.findIndex(
                (photo) => photo.id.toString() === props.initialPhotoId.toString()
            );
            return index !== -1 ? index : 0;
        }
        return 0;
    }

    next = () => {
        const { photosList } = this.props;
        this.setState(prevState => ({
            activeIndex: prevState.activeIndex === photosList.length - 1 ? 0 : prevState.activeIndex + 1
        }));
    };

    previous = () => {
        const { photosList } = this.props;
        this.setState(prevState => ({
            activeIndex: prevState.activeIndex === 0 ? photosList.length - 1 : prevState.activeIndex - 1
        }));
    };

    goToIndex = (index) => {
        this.setState({ activeIndex: index });
    };

    render() {
        const { photosList, isVisible, onClose } = this.props;
        const { activeIndex } = this.state;

        if (photosList.length === 0) {
            return null;
        }

        const currentPhoto = photosList[activeIndex];
        const encodedPath = currentPhoto.url + '/' + encodeURIComponent(currentPhoto.name);

        return (
            <StyledModal isOpen={isVisible} toggle={onClose} size='xl' centered>
                <ModalBody>
                    <CloseButton onClick={onClose}>
                        <CancelIconX />
                    </CloseButton>

                    <CarouselContainer>
                        {photosList.length > 1 && (
                            <PrevButton onClick={this.previous}>
                                <FiChevronLeft />
                            </PrevButton>
                        )}

                        <ImageContainer>
                            <img
                                key={currentPhoto.id}
                                src={encodedPath}
                                alt={currentPhoto.name}
                            />
                        </ImageContainer>

                        {photosList.length > 1 && (
                            <NextButton onClick={this.next}>
                                <FiChevronRight />
                            </NextButton>
                        )}
                    </CarouselContainer>

                    <Caption>
                        <p className='photo-author'>{currentPhoto.author}</p>
                        <p className='photo-name'>{currentPhoto.name}</p>
                    </Caption>

                    {photosList.length > 1 && (
                        <Indicators>
                            {photosList.map((photo, index) => (
                                <Indicator
                                    key={photo.id}
                                    active={index === activeIndex}
                                    onClick={() => this.goToIndex(index)}
                                />
                            ))}
                        </Indicators>
                    )}
                </ModalBody>
            </StyledModal>
        );
    }
}

const StyledModal = styled(Modal)`
    .modal-content {
        background-color: ${colors.white};
        border: none;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;

        @media ${dimension.md} {
            height: 95vh;
            max-height: 95vh;
            border-radius: 0.3rem;
        }
    }

    .modal-body {
        padding: 10px;
        position: relative;
        height: 100%;
        display: flex;
        flex-direction: column;

        @media ${dimension.md} {
            padding: 40px;
        }
    }

    .modal-dialog {
        max-width: 100vw;
        width: 100vw;
        margin: 0;
        max-height: 100vh;
        height: 100vh;

        @media ${dimension.md} {
            max-width: 95vw;
            width: 95vw;
            margin: 2.5vh auto;
            max-height: 95vh;
            height: auto;
        }
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 1000;
    background: ${colors.white};
    border: 1px solid ${colors.gray};
    border-radius: 50%;
    cursor: pointer;
    padding: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

    @media ${dimension.md} {
        top: 10px;
        right: 10px;
        background: transparent;
        border: none;
        box-shadow: none;
        padding: 10px;
    }

    svg {
        height: 20px;
        width: 20px;
        
        path {
            fill: ${colors.red};
        }

        @media ${dimension.md} {
            height: 30px;
            width: 30px;
        }
    }

    &:hover {
        opacity: 0.7;
    }
`;

const CarouselContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    flex: 1;
    overflow: hidden;
`;

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    
    img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
    }
`;

const PrevButton = styled.button`
    position: absolute;
    left: 5px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 100;
    width: 40px;
    height: 40px;
    background-color: ${colors.green};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;

    svg {
        width: 20px;
        height: 20px;
        color: white;

        @media ${dimension.md} {
            width: 32px;
            height: 32px;
        }
    }

    &:hover {
        background-color: ${colors.dark_green};
    }

    &:active {
        transform: translateY(-50%) scale(0.95);
    }

    @media ${dimension.md} {
        width: 70px;
        height: 70px;
        left: 20px;

        &:hover {
            transform: translateY(-50%) scale(1.1);
        }
    }
`;

const NextButton = styled.button`
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 100;
    width: 40px;
    height: 40px;
    background-color: ${colors.green};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;

    svg {
        width: 20px;
        height: 20px;
        color: white;

        @media ${dimension.md} {
            width: 32px;
            height: 32px;
        }
    }

    &:hover {
        background-color: ${colors.dark_green};
    }

    &:active {
        transform: translateY(-50%) scale(0.95);
    }

    @media ${dimension.md} {
        width: 70px;
        height: 70px;
        right: 20px;

        &:hover {
            transform: translateY(-50%) scale(1.1);
        }
    }
`;

const Caption = styled.div`
    text-align: center;
    color: ${colors.darkest_gray};
    padding: 10px 5px 5px 5px;
    min-height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media ${dimension.md} {
        padding: 15px 10px 5px 10px;
        min-height: 80px;
    }

    .photo-author {
        font-weight: bold;
        font-size: 14px;
        margin-bottom: 3px;

        @media ${dimension.md} {
            font-size: 18px;
            margin-bottom: 5px;
        }
    }

    .photo-name {
        font-size: 11px;
        color: ${colors.darker_gray};
        margin-bottom: 0;
        word-break: break-all;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;

        @media ${dimension.md} {
            font-size: 14px;
            -webkit-line-clamp: 3;
        }
    }
`;

const Indicators = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding: 15px 0 5px 0;
`;

const Indicator = styled.button`
    display: block;
    width: 12px;
    height: 12px;
    min-width: 12px;
    min-height: 12px;
    max-width: 12px;
    max-height: 12px;
    border-radius: 50%;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    background-color: ${props => props.active ? colors.green : colors.gray};
    opacity: ${props => props.active ? 1 : 0.5};
    transition: all 0.3s ease;
    box-sizing: border-box;
    flex-shrink: 0;
    aspect-ratio: 1 / 1;
    overflow: hidden;

    &:hover {
        opacity: 1;
        transform: scale(1.2);
    }

    &:focus {
        outline: none;
    }

    &::before,
    &::after {
        display: none;
    }
`;
